import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { getStoredToken, TOKEN_STORAGE_KEY } from '../utils/auth';
import { invalidateSubscriptionGateCache } from '../hooks/useSubscriptionGate';

// // Debug logging for environment variables
// console.log(process.env);
// console.log("REACT_APP_API_URL", process.env.REACT_APP_API_URL);
// console.log("REACT_APP_GOOGLE_CLIENT_ID", process.env.REACT_APP_GOOGLE_CLIENT_ID);
// console.log("REACT_APP_PUBLIC_POSTHOG_KEY", process.env.REACT_APP_PUBLIC_POSTHOG_KEY);
// console.log("REACT_APP_PUBLIC_POSTHOG_HOST", process.env.REACT_APP_PUBLIC_POSTHOG_HOST);

// For browser environments, we need to use the server's public IP or hostname
// Docker service names like "web" only work within the Docker network
const isBrowser = typeof window !== 'undefined';
const isDevelopment = process.env.NODE_ENV === 'development';

// Try to get API URL from environment variables
// First check runtime environment (window.ENV) if in browser, then build-time environment
let BASE_URL: string | undefined;

if (isBrowser && (window as any).ENV?.REACT_APP_API_URL) {
  // Use runtime environment variable from env.js (highest priority)
  BASE_URL = (window as any).ENV.REACT_APP_API_URL;
  console.log('Using runtime API URL from window.ENV:', BASE_URL);
} else {
  // Fallback to build-time environment variables
  BASE_URL = process.env.REACT_APP_API_URL || process.env.API_URL;
  console.log('Using build-time API URL:', BASE_URL);
}

// If we still don't have a BASE_URL or if it contains "web:8000" (which won't work in browser)
if (!BASE_URL || (isBrowser && BASE_URL.includes('web:8000'))) {
  // Use a browser-accessible fallback URL
  if (isDevelopment) {
    // In development, use localhost http
    BASE_URL = 'http://localhost:8000';
    console.log('Using development fallback API URL:', BASE_URL);
  } else {
    // In production, construct the HTTPS URL based on the current hostname
    // Ensure it uses HTTPS
    const hostname = window.location.hostname; // e.g., app.webleads.site
    BASE_URL = `https://${hostname}`;
    console.log('Using production fallback API URL (HTTPS):', BASE_URL);
  }
}

// Append /api to the determined base URL
if (BASE_URL) {
  // Avoid double slashes if the base URL already ends with one
  BASE_URL = BASE_URL.replace(/\/$/, '') + '/api';
}

// console.log('Using API Base URL with /api prefix:', BASE_URL);

const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Ensure cookies are sent with requests
  withCredentials: true,
});

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    // Get token directly from localStorage for each request to ensure we have the latest
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    
    console.log('Token found:', token ? 'yes' : 'no');
    console.log('API Request to:', config.url);
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Added Authorization header');
    } else {
      console.log('No token available for request');
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    console.error('API Error:', error.message);
    console.error('Request URL:', error.config?.url);
    console.error('Status:', error.response?.status);
    console.error('Response data:', error.response?.data);
    
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    
    // Only handle 401 errors for non-auth endpoints 
    // Don't redirect for /api/auth/token or /api/auth/me requests to prevent redirect loops
    if (error.response?.status === 401 && 
        !originalRequest._retry && 
        !originalRequest.url?.includes('/api/auth/token') && 
        !originalRequest.url?.includes('/api/auth/me')) {
      
      console.log('Authentication error detected, redirecting to login');
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      
      // Don't redirect if already on an auth page (login/register/trial)
      const isAuthPagePath = ['/login', '/register', '/trial'].some((path) =>
        window.location.pathname.includes(path)
      );

      if (!isAuthPagePath) {
        window.location.href = '/login';
      }
    }

    // Any user with no subscription hits this on product endpoints (search, dm_unlock, email
    // verification). Route them into the trial picker. Scoped strictly to detail ===
    // "trial_required" so it doesn't hijack the existing per-call 402 -> UpgradeModal flows
    // (smart_filters, dm_unlock_exhausted, etc.) which carry a different `detail`/`feature`.
    if (
      error.response?.status === 402 &&
      (error.response.data as { detail?: string } | undefined)?.detail === 'trial_required' &&
      !window.location.pathname.startsWith('/start-trial')
    ) {
      invalidateSubscriptionGateCache();
      window.location.href = '/start-trial';
    }

    return Promise.reject(error);
  }
);

export default apiClient; 